import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode, withKeys as _withKeys, withModifiers as _withModifiers, createBlock as _createBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "flex flex-col gap-3 text-left p-3 border border-gray-400 rounded" }
const _hoisted_2 = { class: "flex" }
const _hoisted_3 = { class: "flex-1 truncate font-semibold" }
const _hoisted_4 = { class: "flex flex-col gap-2" }
const _hoisted_5 = {
  key: 0,
  class: "flex-1 truncate"
}
const _hoisted_6 = { class: "bg-gray-400 text-white px-1 py-0.5 rounded-md font-mono" }
const _hoisted_7 = ["value"]
const _hoisted_8 = { class: "shrink-0 flex items-center gap-2" }
const _hoisted_9 = ["disabled"]

import { CheckIcon } from "@heroicons/vue/24/outline";
import { PencilIcon, PlusIcon, TrashIcon, XMarkIcon } from "@heroicons/vue/24/solid";
import { v4 as uuidv4 } from "uuid";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useConfirmationModal } from "shared/composables/toast";
import { CriticalPathEx, CriticalPathContext, CriticalPathTag } from "shared/types/CriticalPath";
import { useCriticalPathTagLetterById } from "shared/views/critical_path/composables";

const addedTagId = "added";


export default /*#__PURE__*/_defineComponent({
  __name: 'CriticalPathTags',
  props: {
    criticalPath: {},
    context: {},
    selectedNodeIds: {}
  },
  emits: ["change", "close"],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emit = __emit;

const { t } = useI18n();

const editedTag = ref<CriticalPathTag | null>(null);

const showConfirmationModal = useConfirmationModal();

const criticalPathTagLetterById = useCriticalPathTagLetterById(computed(() => props.criticalPath));

const tagsToShow = computed<CriticalPathTag[]>(() => {
  if (!editedTag.value || editedTag.value._id !== addedTagId) {
    return props.criticalPath.tags;
  }
  return [...props.criticalPath.tags, editedTag.value];
});

const focusInputDelayed = () => {
  setTimeout(() => {
    const inputElement = document.querySelector(".oaiCriticalPathTagNameInput") as HTMLInputElement;
    if (inputElement) {
      inputElement.focus();
      inputElement.select();
    }
  }, 100);
};

const handleNameInput = (event: Event) => {
  if (!editedTag.value) {
    return;
  }
  const inputElement = event.currentTarget as HTMLInputElement;
  editedTag.value.name = inputElement.value;
};

const handleEdit = (tag: CriticalPathTag) => {
  if (editedTag.value) {
    return;
  }
  editedTag.value = { ...tag };
  focusInputDelayed();
};

const defaultNewTagName = computed(() => {
  if (props.selectedNodeIds[0]) {
    const node = props.context.criticalPathNodesById[props.selectedNodeIds[0]];
    if (node) {
      const name = props.context.tagsById[node.section_id]?.name;
      if (name) {
        return name;
      }
    }
  }
  return t("analytics.critical_path.new_tag");
});

const getFallbackTagName = () => {
  if (!editedTag.value || editedTag.value._id === addedTagId) {
    return defaultNewTagName.value;
  }
  const oldTag = props.criticalPath.tags.find((tag) => tag._id === editedTag.value?._id);
  return oldTag?.name || defaultNewTagName.value;
};

const handleEditConfirm = () => {
  if (!editedTag.value) {
    return;
  }
  const isAdding = editedTag.value._id === addedTagId;
  const finalTag = {
    ...editedTag.value,
    _id: isAdding ? uuidv4() : editedTag.value._id,
    name: editedTag.value.name || getFallbackTagName(),
  };
  const newTags = isAdding
    ? [...props.criticalPath.tags, finalTag]
    : props.criticalPath.tags.map((tag) => (tag._id === finalTag?._id ? finalTag : tag));
  emit("change", {
    ...props.criticalPath,
    tags: newTags,
  });
  editedTag.value = null;
};

const handleAddClick = () => {
  if (editedTag.value) {
    return;
  }
  editedTag.value = {
    _id: addedTagId,
    name: defaultNewTagName.value,
    type: "component_similarity",
  };
  focusInputDelayed();
};

const handleDelete = (tag: CriticalPathTag) => {
  if (editedTag.value) {
    return;
  }
  showConfirmationModal({
    header: "Confirmation",
    message: `Tag "${tag.name}" will be deleted`,
    confirmAction: "Delete tag",
    cancelAction: "Cancel",
  }).then((confirmed) => {
    if (confirmed) {
      emit("change", {
        ...props.criticalPath,
        tags: props.criticalPath.tags.filter(({ _id }) => _id !== tag._id),
        nodes: props.criticalPath.nodes.map((node) => ({
          ...node,
          tags: node.tags.filter((tagId) => tagId !== tag._id),
        })),
      });
    }
  });
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, _toDisplayString(_unref(t)("analytics.critical_path.similar_components")), 1),
      _createVNode(_unref(XMarkIcon), {
        class: "h-6 w-6 cursor-pointer shrink-0",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('close')))
      })
    ]),
    _createElementVNode("div", _hoisted_4, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tagsToShow.value, (tag) => {
        return (_openBlock(), _createElementBlock("div", {
          key: tag._id,
          class: "flex items-center gap-5"
        }, [
          (editedTag.value?._id !== tag._id)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _createElementVNode("span", _hoisted_6, _toDisplayString(_unref(criticalPathTagLetterById)[tag._id]), 1),
                _createTextVNode(" " + _toDisplayString(tag.name), 1)
              ]))
            : _createCommentVNode("", true),
          (editedTag.value?._id === tag._id)
            ? (_openBlock(), _createElementBlock("input", {
                key: 1,
                type: "text",
                class: "oaiCriticalPathTagNameInput flex-1 rounded-md border-0 py-1 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-yellow-600 text-sm leading-6",
                value: editedTag.value.name,
                onInput: handleNameInput,
                onKeydown: [
                  _withKeys(handleEditConfirm, ["enter"]),
                  _cache[1] || (_cache[1] = _withKeys(_withModifiers(($event: any) => (editedTag.value = null), ["stop"]), ["esc"]))
                ]
              }, null, 40, _hoisted_7))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_8, [
            (editedTag.value?._id === tag._id)
              ? (_openBlock(), _createBlock(_unref(CheckIcon), {
                  key: 0,
                  class: "w-5 h-5 cursor-pointer text-gray-600 hover:text-yellow-600 shrink-0",
                  onClick: handleEditConfirm
                }))
              : _createCommentVNode("", true),
            (editedTag.value?._id === tag._id)
              ? (_openBlock(), _createBlock(_unref(XMarkIcon), {
                  key: 1,
                  class: "w-5 h-5 cursor-pointer text-gray-600 hover:text-yellow-600 shrink-0",
                  onClick: _cache[2] || (_cache[2] = ($event: any) => (editedTag.value = null))
                }))
              : _createCommentVNode("", true),
            (editedTag.value?._id !== tag._id)
              ? (_openBlock(), _createBlock(_unref(PencilIcon), {
                  key: 2,
                  class: _normalizeClass(["w-4 h-4 shrink-0", 
              editedTag.value ? 'text-gray-200' : 'text-gray-600 hover:text-yellow-600 cursor-pointer'
            ]),
                  onClick: ($event: any) => (handleEdit(tag))
                }, null, 8, ["class", "onClick"]))
              : _createCommentVNode("", true),
            (editedTag.value?._id !== tag._id)
              ? (_openBlock(), _createBlock(_unref(TrashIcon), {
                  key: 3,
                  class: _normalizeClass(["h-4 w-4 shrink-0", 
              editedTag.value ? 'text-gray-200' : 'text-gray-600 hover:text-yellow-600 cursor-pointer'
            ]),
                  onClick: ($event: any) => (handleDelete(tag))
                }, null, 8, ["class", "onClick"]))
              : _createCommentVNode("", true)
          ])
        ]))
      }), 128))
    ]),
    _createElementVNode("div", null, [
      _createElementVNode("button", {
        class: "py-0.5 px-3 border rounded cursor-pointer border-gray-300 flex items-center gap-1 disabled:text-gray-300 disabled:cursor-default",
        onClick: handleAddClick,
        disabled: !!editedTag.value
      }, [
        _createVNode(_unref(PlusIcon), { class: "h-5 w-5" }),
        _createTextVNode(_toDisplayString(_unref(t)("buttons.add")), 1)
      ], 8, _hoisted_9)
    ])
  ]))
}
}

})